import React, {
  createContext, useContext, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { ThemeContext } from '../../../constant/theme';
import { LanguageContext } from '../../../constant/LanguageContext';
import { useMessenger } from '../../../context/MessengerContext';
import { useNotifications } from '../../../context/NotificationContext';
import AlertModal from '../../../components/AlertModal';
import useMessengerInit from '../hooks/useMessengerInit';
import useMessages from '../hooks/useMessages';
import { useProfile } from '../../../hooks/useProfile';
import { isSameUser } from '../utils/userHelpers';
import { translations } from '../constants/translations';
import { getColors } from '../style/messengerColors';
import useProfilePics from '../hooks/useProfilePics';

const MiniChatContext = createContext();

export const useMiniChat = () => {
  const ctx = useContext(MiniChatContext);
  if (!ctx) throw new Error('useMiniChat must be used within a MiniChatProvider');
  return ctx;
};

/** Plays a short two-tone chime via the Web Audio API — no new asset/dependency. */
const playNotificationSound = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    [880, 660].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.001, now + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.12, now + i * 0.12 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.12 + 0.18);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.2);
    });
    setTimeout(() => ctx.close().catch(() => {}), 500);
  } catch {
    // Best-effort — autoplay policies or missing AudioContext shouldn't break chat.
  }
};

/**
 * Owns ONLY the Mini Chat Dock's own concerns: which floating windows are
 * open/minimized, and routing the app's ALREADY-BRIDGED realtime events
 * (see NotificationContext.js) into the SAME `useMessages` functions the
 * full messenger page uses. It does not open a socket, does not fetch
 * conversations on its own beyond the one reused `useMessengerInit` call,
 * and never touches `MessengerContext.selectedUser` (owned exclusively by
 * the full page's own selection flow).
 */
export const MiniChatProvider = ({ children }) => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const isRTL = language === 'ar';
  const t = translations[language];
  const c = useMemo(() => getColors(theme), [theme]);

  const {
    conversations, setConversations,
    messages, setMessages,
    onlineUsers, setOnlineUsers,
    currentUser, setCurrentUser,
    employeeProfilePics, setEmployeeProfilePics,
    isMessengerPageOpen,
    // The full Messenger page restores this exact conversation on mount
    // (see MessengerPage.jsx's `_userSelected` check) — setting it right
    // before navigating there from the mini dock is what makes "open full
    // inbox" land on the same conversation instead of an empty inbox.
    setSelectedUser: setSharedSelectedUser,
    chatPrefs,
  } = useMessenger();

  const { socket, isConnected, miniChatDisabled, setMiniChatDisabled } = useNotifications();
  const { profile, profileLoading } = useProfile();

  const [openWindows, setOpenWindows] = useState([]); // [{ chatId, minimized }]
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [alertState, setAlertState] = useState({
    open: false, title: '', message: '', variant: 'info', showCancel: false, onConfirm: null,
  });

  // Dummy input state — this instance of useMessages() is only ever used for
  // its selectedUser-INDEPENDENT functions (handleIncomingMessage,
  // handleReactionEvent, markMessagesAsRead). Compose/send/typing for a
  // specific conversation is handled by that window's OWN useMessages()
  // instance in ChatWindow.jsx, which has the real selectedUser.
  const [, setDummyInput] = useState('');

  // Same call ORDER as MessengerPage.jsx: useMessages first (with the same
  // `allEmployees: []`/`userNamesCache: {}` stubs it uses — a brand-new
  // inbound DM from someone not yet in `conversations` may briefly show as
  // "User <id>" until a refresh; this is an existing, matched limitation,
  // not a new one), THEN useMessengerInit second, wiring its real
  // `fetchMessagesForUser` back in so opening an uncached conversation from
  // the dock actually fetches its history.
  const messagesState = useMessages({
    socket, isConnected, currentUser, selectedUser: null,
    messages, setMessages, conversations, setConversations,
    allEmployees: [], userNamesCache: {},
    onlineUsers, messageInput: '', setMessageInput: setDummyInput,
    t, setAlertState,
  });

  const initState = useMessengerInit({
    currentUser, setCurrentUser, conversations, setConversations, messages, setMessages,
    onlineUsers,
    employeeProfilePics,
    fetchEmployeeProfilePic: async () => null, // matches MessengerPage.jsx's own stub — see note below
    fetchMessagesForUser: messagesState.fetchMessagesForUser,
    isSameUser,
    language,
    t,
  });

  // Initial load (auth + conversations + employees), reusing the EXACT
  // function the full messenger page calls — with no socket argument, so it
  // never opens a second connection (see useMessengerInit.js:266).
  //
  // This provider is mounted unconditionally at the app root (same as
  // ClientBadge/IdleSessionGuard), including on /login before authentication
  // completes — so the guard can't be a permanent "ran once" ref; it has to
  // re-check once a real profile becomes available, mirroring exactly how
  // NotificationContext gates its own socket effect on `profile?.infinityCode`.
  const initializedForRef = useRef(null);
  useEffect(() => {
    if (profileLoading || !profile?.infinityCode) return;
    if (initializedForRef.current === profile.infinityCode) return;
    initializedForRef.current = profile.infinityCode;
    initState.initializeMessaging();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.infinityCode, profileLoading]);

  // Proactively wipe the previous account's data the instant logout fires,
  // instead of waiting for the next login's fetch to overwrite it — this
  // provider (and MessengerContext's shared state) never unmounts across a
  // login/logout cycle in the same SPA session, so without this the old
  // account's conversations/messages/avatars would otherwise still be
  // sitting in memory (and briefly visible) right after the new account logs in.
  useEffect(() => {
    const onLogout = () => {
      initializedForRef.current = null;
      setConversations([]);
      setMessages({});
      setOnlineUsers(new Set());
      setEmployeeProfilePics({});
      setCurrentUser(null);
      setOpenWindows([]);
    };
    window.addEventListener('hrms:auth:logout', onLogout);
    return () => window.removeEventListener('hrms:auth:logout', onLogout);
  }, [setConversations, setMessages, setOnlineUsers, setEmployeeProfilePics, setCurrentUser]);

  const profilePicsState = useProfilePics({
    token: initState.token,
    conversations,
    employeeProfilePics,
    setEmployeeProfilePics,
  });

  // Re-map the init state to use the actual picture fetcher instead of a stub
  const fetchPic = profilePicsState.fetchEmployeeProfilePic;
  initState.fetchEmployeeProfilePic = fetchPic;
  const isGroupChatId = (chatId) => typeof chatId === 'string' && chatId.startsWith('group-');

  const handleGroupStatusUpdate = useCallback((data) => {
    const { groupId, messages: statusUpdates } = data || {};
    if (!statusUpdates || !Array.isArray(statusUpdates)) return;
    const fullGroupId = groupId?.startsWith('group-') ? groupId : `group-${groupId}`;
    setMessages((prev) => {
      const list = prev[fullGroupId];
      if (!list) return prev;
      let changed = false;
      const updated = list.map((msg) => {
        const match = statusUpdates.find((u) => u.messageId === msg.id);
        if (match && msg.sent && msg.status !== match.status) {
          changed = true;
          return { ...msg, status: match.status };
        }
        return msg;
      });
      return changed ? { ...prev, [fullGroupId]: updated } : prev;
    });
  }, [setMessages]);

  const handleGroupMessageDeleted = useCallback((data) => {
    const { messageId, groupId } = data || {};
    const fullGroupId = groupId?.startsWith('group-') ? groupId : `group-${groupId}`;
    setMessages((prev) => {
      const list = prev[fullGroupId];
      if (!list) return prev;
      const filtered = list.filter((m) => m.id !== parseInt(messageId, 10) && m.id !== messageId);
      return filtered.length === list.length ? prev : { ...prev, [fullGroupId]: filtered };
    });
  }, [setMessages]);

  // ── Route the app's already-bridged realtime events into the shared,
  // reused useMessages() functions. ONE listener per event type, app-wide
  // (not one per open window) — avoids N-times-redundant invocation.
  useEffect(() => {
    const notify = (data, senderName) => {
      const myCode = String(currentUser?.infinityCode || '');
      const senderId = String(data?.sender?.infinityCode || data?.senderId || data?.senderCode || '');
      if (senderId && senderId === myCode) return; // never notify on our own message
      playNotificationSound();
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const title = senderName || data?.senderName || data?.sender?.sr || t.newMessages || 'New message';
        const body = data?.content
          || (data?.type === 'image' ? '📷 Image'
            : data?.type === 'voice' ? '🎤 Voice message'
            : data?.mediaName || 'Attachment');
        try { new Notification(title, { body, silent: true }); } catch { /* ignore */ }
      }
    };

    const onReceived = (e) => { messagesState.handleIncomingMessage(e.detail, currentUser); notify(e.detail); };
    const onGroupMessage = (e) => { messagesState.handleIncomingMessage(e.detail, currentUser); notify(e.detail); };
    const onReaction = (e) => messagesState.handleReactionEvent(e.detail, currentUser);
    const onGroupReaction = (e) => messagesState.handleReactionEvent(e.detail, currentUser);
    const onMessagesRead = (e) => { if (e.detail?.userId) messagesState.markMessagesAsRead(e.detail.userId); };
    const onGroupStatusUpdate = (e) => handleGroupStatusUpdate(e.detail);
    const onGroupMessageDeleted = (e) => handleGroupMessageDeleted(e.detail);
    const onTyping = (e) => {
      const { senderId, isTyping } = e.detail || {};
      if (!senderId) return;
      setTypingUsers((prev) => {
        const next = new Set(prev);
        if (isTyping) next.add(senderId); else next.delete(senderId);
        return next;
      });
    };
    const onUsersOnline = (e) => {
      if (Array.isArray(e.detail)) setOnlineUsers(new Set(e.detail));
    };
    const onUserOnline = (e) => {
      if (e.detail?.userId != null) setOnlineUsers((prev) => new Set(prev).add(String(e.detail.userId)));
    };
    const onUserOffline = (e) => {
      if (e.detail?.userId != null) {
        setOnlineUsers((prev) => {
          const next = new Set(prev);
          next.delete(String(e.detail.userId));
          return next;
        });
      }
    };

    window.addEventListener('hrms:private-message:received', onReceived);
    window.addEventListener('hrms:chat_group:message', onGroupMessage);
    window.addEventListener('hrms:private-message:reaction', onReaction);
    window.addEventListener('hrms:chat_group:reaction', onGroupReaction);
    window.addEventListener('hrms:private-messages:read', onMessagesRead);
    window.addEventListener('hrms:chat_group:status_update', onGroupStatusUpdate);
    window.addEventListener('hrms:chat_group:message_deleted', onGroupMessageDeleted);
    window.addEventListener('hrms:private-message:typing', onTyping);
    window.addEventListener('hrms:users:online', onUsersOnline);
    window.addEventListener('hrms:user:online', onUserOnline);
    window.addEventListener('hrms:user:offline', onUserOffline);

    // Request browser-notification permission once, best-effort. No existing
    // settings UI for this — silently no-ops if unsupported or dismissed.
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    return () => {
      window.removeEventListener('hrms:private-message:received', onReceived);
      window.removeEventListener('hrms:chat_group:message', onGroupMessage);
      window.removeEventListener('hrms:private-message:reaction', onReaction);
      window.removeEventListener('hrms:chat_group:reaction', onGroupReaction);
      window.removeEventListener('hrms:private-messages:read', onMessagesRead);
      window.removeEventListener('hrms:chat_group:status_update', onGroupStatusUpdate);
      window.removeEventListener('hrms:chat_group:message_deleted', onGroupMessageDeleted);
      window.removeEventListener('hrms:private-message:typing', onTyping);
      window.removeEventListener('hrms:users:online', onUsersOnline);
      window.removeEventListener('hrms:user:online', onUserOnline);
      window.removeEventListener('hrms:user:offline', onUserOffline);
    };
  }, [messagesState, currentUser, setOnlineUsers, handleGroupStatusUpdate, handleGroupMessageDeleted, t]);

  const openChat = useCallback((conversation) => {
    if (!conversation?.id) return;
    setOpenWindows((prev) => {
      const exists = prev.some((w) => String(w.chatId) === String(conversation.id));
      if (exists) {
        // Already open (just minimized, most likely) — restore it in place.
        return prev.map((w) => (
          String(w.chatId) === String(conversation.id) ? { ...w, minimized: false } : w
        ));
      }
      // Only one floating window at a time: opening a different conversation
      // replaces whichever one is currently open, rather than stacking.
      return [{ chatId: conversation.id, minimized: false }];
    });
    // Reuses the existing "on open" side effects (message fetch + unread clear).
    initState.handleUserSelect(conversation);
  }, [initState]);

  const closeChat = useCallback((chatId) => {
    setOpenWindows((prev) => prev.filter((w) => String(w.chatId) !== String(chatId)));
  }, []);

  const minimizeChat = useCallback((chatId) => {
    setOpenWindows((prev) => prev.map((w) => (
      String(w.chatId) === String(chatId) ? { ...w, minimized: true } : w
    )));
  }, []);

  const restoreChat = useCallback((chatId) => {
    setOpenWindows((prev) => prev.map((w) => (
      String(w.chatId) === String(chatId) ? { ...w, minimized: false } : w
    )));
  }, []);

  const value = useMemo(() => ({
    openWindows, openChat, closeChat, minimizeChat, restoreChat, isGroupChatId,
    conversations, setConversations, messages, setMessages,
    onlineUsers, employeeProfilePics, currentUser,
    typingUsers, socket, isConnected, isMessengerPageOpen,
    miniChatDisabled, setMiniChatDisabled,
    c, isRTL, t, theme, language,
    setAlertState, chatPrefs, setSharedSelectedUser,
    // Exposed for the dock's "compose" (new message) picker — the SAME
    // visibility-scoped employee list the full Messenger page uses.
    allEmployees: initState.allEmployees,
  }), [
    openWindows, openChat, closeChat, minimizeChat, restoreChat,
    conversations, setConversations, messages, setMessages,
    onlineUsers, employeeProfilePics, currentUser,
    typingUsers, socket, isConnected, isMessengerPageOpen,
    miniChatDisabled, setMiniChatDisabled,
    c, isRTL, t, theme, language, chatPrefs, setSharedSelectedUser,
    initState.allEmployees,
  ]);

  return (
    <MiniChatContext.Provider value={value}>
      {children}
      <AlertModal
        open={alertState.open}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        showCancel={alertState.showCancel}
        onConfirm={() => {
          if (alertState.onConfirm) alertState.onConfirm();
          else setAlertState((prev) => ({ ...prev, open: false }));
        }}
        onClose={() => setAlertState((prev) => ({ ...prev, open: false }))}
        theme={theme}
        language={language}
      />
    </MiniChatContext.Provider>
  );
};

export default MiniChatProvider;
